import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { ArrowRight } from "lucide-react";
import BrandMark, { BrandMarkDark } from "../components/BrandMark";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/");
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || "Přihlášení selhalo");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 pt-safe pb-safe">
            {/* Left: form */}
            <div className="lg:col-span-2 flex items-center justify-center px-8 py-12 bg-white">
                <div className="w-full max-w-sm">
                    <div className="mb-12">
                        <BrandMarkDark />
                    </div>

                    <div className="label-eyebrow mb-2">Přihlášení</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900 mb-2">Vítejte zpět.</h1>
                    <p className="text-sm text-slate-600 mb-8">Spravujte zakázky, ceník a rozpočty na jednom místě.</p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="label-eyebrow block mb-2">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email"
                                className="w-full px-4 py-3 border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm"
                                placeholder="vy@stavbafirma.cz"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-2">Heslo</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password"
                                className="w-full px-4 py-3 border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm"
                                placeholder="••••••••"
                            />
                        </div>

                        {err && (
                            <div className="border-l-4 border-red-600 bg-red-50 px-4 py-2 text-sm text-red-700" data-testid="login-error">{err}</div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit"
                            className="w-full flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 font-semibold uppercase tracking-wider text-sm transition-colors disabled:opacity-50 rounded-sm"
                        >
                            {loading ? "Přihlašuji…" : "Přihlásit se"}
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-slate-200 text-sm text-slate-600 flex items-center justify-between gap-4 flex-wrap">
                        <Link to="/zapomenute-heslo" data-testid="link-forgot-password" className="font-semibold text-orange-600 hover:underline">Zapomněli jste heslo?</Link>
                        <span className="text-xs text-slate-500" data-testid="invite-only-note">Nový účet jen přes pozvánku od vlastníka firmy.</span>
                    </div>
                </div>
            </div>

            {/* Right: image */}
            <div
                className="hidden lg:block lg:col-span-3 relative bg-slate-900"
                style={{
                    backgroundImage:
                        "linear-gradient(135deg, rgba(7,7,6,0.85), rgba(7,7,6,0.55)), url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
                    <div className="w-12 h-[2px] gold-rule mb-6"></div>
                    <div className="label-eyebrow text-orange-400 mb-3">Ceramixa style s.r.o.</div>
                    <h2 className="brand-serif text-6xl font-medium leading-[1.05] mb-4 max-w-xl">
                        Stavba i&nbsp;detail<br/>v <span className="italic gold-text font-semibold">každém</span> obkladu.
                    </h2>
                    <p className="text-slate-300 max-w-lg leading-relaxed text-sm">Evidence zakázek, fotek a dokumentů. Vlastní ceník. Profesionální rozpočty a faktury s českou QR Platbou.</p>
                </div>
            </div>
        </div>
    );
}
